import { useEffect } from "react";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Card, Spinner, Tabs } from "../../../../../shared";
import { CardHeader } from "../../../../../shared/Card/CardHeader";
import { capitalizeFirstLetter } from "../../../../../utils";
import {
  useCreateAdminCustomerBillingPortalSessionMutation,
  useCreateAdminCustomerCheckoutSessionMutation,
  useGetAdminCustomerBillingSummaryQuery,
  useUpdateAdminCustomerBillingInvoiceMutation,
} from "../../../../../redux/services/admin/adminApi";
import {
  BILLING_INVOICE_FETCH_LIMIT,
  isManualBillingCollection,
} from "../../../../Settings/Billing/billing.utils";
import { BillingInvoiceHistory } from "../../../../Settings/Billing/BillingInvoiceHistory";
import { ManualBillingInvoiceForm } from "./ManualBillingInvoiceForm";

const formatLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => capitalizeFirstLetter(part))
    .join(" ");

const CustomerBilling = ({ customer }) => {
  const customerId = customer?._id;
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading, refetch: refetchSummary } = useGetAdminCustomerBillingSummaryQuery(
    {
      id: customerId,
      limit: BILLING_INVOICE_FETCH_LIMIT,
    },
    {
      skip: !customerId,
    }
  );
  const [createPortalSession, { isLoading: portalLoading }] =
    useCreateAdminCustomerBillingPortalSessionMutation();
  const [createCheckoutSession, { isLoading: checkoutLoading }] =
    useCreateAdminCustomerCheckoutSessionMutation();
  const [updateBillingInvoice, { isLoading: invoiceUpdateLoading }] =
    useUpdateAdminCustomerBillingInvoiceMutation();

  /**
   * Stripe Checkout return handler for tenant billing.
   *
   * Stripe redirects back with ?checkout=success or ?checkout=cancel after
   * a session completes. We surface a toast, refetch the tenant billing
   * summary, then strip the param so it doesn't re-fire on rerender.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutResult = params.get("checkout");
    if (!checkoutResult) return;

    if (checkoutResult === "success") {
      toast.success(
        "Tenant subscription started. Stripe may take a few seconds to update billing details here."
      );
      refetchSummary();
    } else if (checkoutResult === "cancel") {
      toast.info(
        "Tenant checkout was cancelled. No changes were made to billing."
      );
    }

    params.delete("checkout");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash || "",
      },
      { replace: true }
    );
  }, [location.search, location.pathname, location.hash, navigate, refetchSummary]);

  const billingSummary = data?.billing || {};
  const billingCustomer = data?.customer || {};
  const billingAccount = billingSummary?.billingAccount || {};
  const entitlements = billingSummary?.entitlements || {};
  const offer = billingSummary?.offer || null;
  const catalog = billingSummary?.catalog || {};
  const subscription = billingSummary?.subscription || null;
  const paymentMethods = billingSummary?.paymentMethods || [];
  const invoices = billingSummary?.invoices || [];
  const actions = billingSummary?.actions || {};
  const catalogOffers = catalog?.offers || [];
  const selfServeOffers = catalog?.selfServeOffers || [];
  const currentPlanKey = billingAccount?.planKey || offer?.key || null;
  const currentOffer =
    offer || catalogOffers.find((item) => item.key === currentPlanKey) || null;
  const selectedPaymentMethod = paymentMethods[0];
  const payMethodType = selectedPaymentMethod?.type;
  const payMethod = selectedPaymentMethod?.[payMethodType] || {};
  const isCard = payMethodType === "card";
  const isBank = payMethodType === "us_bank_account";
  const collectionMethod =
    billingAccount?.collectionMethod || "manual_invoice";

  const formatCollectionMethod = (value) => {
    if (value === "manual_invoice") {
      return "Manual invoice / contract";
    }

    if (value === "stripe_subscription") {
      return "Stripe subscription";
    }

    return formatLabel(value || "manual_invoice");
  };

  const openBillingPortal = async () => {
    try {
      const returnUrl = `${window.location.origin}/admin/customers/${customerId}?customer-show=Billing`;
      const res = await createPortalSession({
        id: customerId,
        returnUrl,
      }).unwrap();
      if (res?.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Could not open tenant billing portal. Please try again.");
      }
    } catch (error) {
      console.error("Failed to open tenant billing portal:", error);
      toast.error(
        error?.data?.message ||
          "Failed to open tenant billing portal. Please try again or contact support."
      );
    }
  };

  const startCheckout = async (planKey) => {
    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/admin/customers/${customerId}?customer-show=Billing&checkout=success`;
      const cancelUrl = `${baseUrl}/admin/customers/${customerId}?customer-show=Billing&checkout=cancel`;
      const res = await createCheckoutSession({
        id: customerId,
        planKey: planKey || actions?.defaultCheckoutPlanKey || catalog?.defaultCheckoutPlanKey,
        successUrl,
        cancelUrl,
      }).unwrap();

      if (res?.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Could not start tenant checkout. Please try again.");
      }
    } catch (error) {
      console.error("Failed to create tenant checkout session:", error);
      toast.error(
        error?.data?.message ||
          "Failed to start tenant checkout. Please try again or contact support."
      );
    }
  };

  const cycle = subscription?.items?.data?.[0]?.price?.recurring?.interval;
  const rawAmount = subscription?.items?.data?.[0]?.price?.unit_amount;
  const planCost =
    typeof rawAmount === "number" ? (rawAmount / 100).toFixed(2) : null;
  const planName =
    billingAccount?.planName ||
    currentOffer?.name ||
    (collectionMethod === "manual_invoice" ? "Custom contract" : "Custom");
  const billingModelLabel =
    billingAccount?.billingModel === "flat_org"
      ? "Flat monthly fee per organization"
      : "Custom billing model";
  const canManagePortal = Boolean(actions?.canManagePortal);
  const canStartCheckout = Boolean(actions?.canStartCheckout);
  const statusLabel = isLoading
    ? "Loading"
    : formatLabel(
        billingAccount?.status === "manual"
          ? "manual billing"
          : billingAccount?.status || "pending"
      );
  const statusDescription =
    collectionMethod === "manual_invoice"
      ? "This tenant is staying on manual billing outside AttuneLogic while access continues to follow plan tier and feature flags."
      : canManagePortal
      ? entitlements?.requiresBillingAttention
        ? "Billing needs attention for this tenant, but access stays monitor-only while rollout remains backward compatible."
        : "Manage subscription, payment methods, and hosted invoices through Stripe."
      : "Start a subscription for this tenant or keep the current manual billing setup.";

  const statusBanner = isLoading ? (
    <div className="border rounded-lg p-3 bg-gray-50">
      <Spinner />
    </div>
  ) : (
    <div className="border rounded-lg p-3 bg-gray-50 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <div className="text-sm text-gray-600">Billing status</div>
        <div className="font-semibold text-gray-900">{statusLabel}</div>
        <div className="text-xs text-gray-500">{statusDescription}</div>
      </div>
      <div className="flex gap-2">
        {canStartCheckout &&
        !isManualBillingCollection(collectionMethod) &&
        selfServeOffers.length > 0 ? (
          <Button size="sm" onClick={() => startCheckout()} disabled={checkoutLoading}>
            {checkoutLoading ? "Opening..." : "Start subscription"}
          </Button>
        ) : null}
        {canManagePortal ? (
          <Button
            type="alternative"
            size="sm"
            onClick={openBillingPortal}
            disabled={portalLoading}
          >
            {portalLoading ? "Opening..." : "Manage billing"}
          </Button>
        ) : null}
      </div>
    </div>
  );

  const overviewContent = (
    <div className="space-y-3">
      {statusBanner}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 auto-rows-fr gap-3">
        <Card
          header={<CardHeader title="Plan" />}
          content={
            isLoading ? (
              <Spinner />
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Tenant</div>
                  <div className="font-semibold text-gray-800">
                    {billingCustomer?.name || customer?.name}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Commercial plan</div>
                  <div className="font-semibold text-gray-800">{planName}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Billing cycle</div>
                  <div className="font-semibold text-gray-800">
                    {cycle
                      ? capitalizeFirstLetter(cycle)
                      : offer?.interval
                      ? capitalizeFirstLetter(offer.interval)
                      : "Monthly"}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Amount</div>
                  <div className="font-semibold text-gray-800">
                    {planCost ? `$${planCost}` : billingModelLabel}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Product tier</div>
                  <div className="font-semibold text-gray-800">
                    {formatLabel(billingAccount?.planTier || "tier1")}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Payment collection</div>
                  <div className="font-semibold text-gray-800">
                    {formatCollectionMethod(collectionMethod)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Release channel</div>
                  <div className="font-semibold text-gray-800">
                    {formatLabel(
                      billingCustomer?.releaseChannel ||
                        customer?.releaseChannel ||
                        "stable"
                    )}
                  </div>
                </div>
              </div>
            )
          }
        />
        <Card
          header={
            <CardHeader
              title="Payment method"
              actions={
                canManagePortal ? (
                  <Button
                    type="light"
                    size="xs"
                    onClick={openBillingPortal}
                    disabled={portalLoading}
                  >
                    {portalLoading ? "Opening..." : "Update"}
                  </Button>
                ) : null
              }
            />
          }
          content={
            isLoading ? (
              <Spinner />
            ) : collectionMethod === "manual_invoice" ? (
              <div className="text-sm text-gray-600 space-y-2">
                <div>
                  This tenant is billed manually outside the platform, so there is no Stripe
                  payment method on file to manage here yet.
                </div>
                <div>
                  Move them to a self-serve plan only when they are ready to manage payments in
                  AttuneLogic.
                </div>
              </div>
            ) : !selectedPaymentMethod ? (
              <div className="text-sm text-gray-600">
                No payment method is on file yet for this tenant.
              </div>
            ) : (
              <div className="grid grid-cols-5 border p-3 rounded-lg">
                <div className="col-span-5">
                  <div className="text-sm font-semibold text-gray-700">
                    {isBank
                      ? "Bank Account"
                      : payMethod?.brand
                      ? capitalizeFirstLetter(payMethod.brand)
                      : "Payment method"}
                  </div>
                  <div className="text-xs font-semibold text-gray-700">
                    {isBank && payMethod.bank_name}
                    {isCard && `**** **** **** ${payMethod.last4}`}
                  </div>
                  <div className="text-xs text-gray-500">
                    {isBank && `Ending in ${payMethod.last4}`}
                    {isCard &&
                      `Expiry on ${`${payMethod.exp_month} ${payMethod.exp_year}`}`}
                  </div>
                  <div className="text-xs text-gray-500 flex">
                    <EnvelopeIcon className="w-3 mr-1" />
                    {selectedPaymentMethod?.billing_details?.email ||
                      "Billing email managed in Stripe"}
                  </div>
                </div>
              </div>
            )
          }
        />
        <Card
          header={<CardHeader title="Access impact" />}
          content={
            isLoading ? (
              <Spinner />
            ) : (
              <div className="space-y-2 text-sm text-gray-600">
                <div>
                  Product access remains available while billing stays monitor-only for this rollout.
                </div>
                <div>
                  Billing attention required:{" "}
                  <span className="font-semibold text-gray-800">
                    {entitlements?.requiresBillingAttention ? "Yes" : "No"}
                  </span>
                </div>
                <div>
                  Gating source:{" "}
                  <span className="font-semibold text-gray-800">
                    {entitlements?.gatingSource || "planTier_and_featureFlags"}
                  </span>
                </div>
              </div>
            )
          }
        />
      </div>
    </div>
  );

  const markInvoicePaid = async (invoiceId) => {
    try {
      await updateBillingInvoice({
        id: customerId,
        invoiceId,
        status: "paid",
      }).unwrap();
      toast.success("Invoice marked as paid.");
      refetchSummary();
    } catch (error) {
      console.error("Failed to update billing invoice:", error);
      toast.error(error?.data?.message || "Could not update invoice.");
    }
  };

  const invoicesContent = (
    <div className="space-y-3">
      {collectionMethod === "manual_invoice" ? (
        <ManualBillingInvoiceForm customerId={customerId} onCreated={refetchSummary} />
      ) : !canManagePortal ? (
        <div className="border rounded-lg p-3 bg-gray-50 text-sm text-gray-600">
          Hosted subscription invoices will appear here after a subscription is started.
        </div>
      ) : null}
      <BillingInvoiceHistory
        invoices={invoices}
        loading={isLoading}
        collectionMethod={collectionMethod}
        canManagePortal={canManagePortal}
        renderInvoiceActions={(invoice) =>
          invoice?.source === "manual" && invoice?.status !== "paid" ? (
            <Button
              title={invoiceUpdateLoading ? "Saving..." : "Mark paid"}
              type="light"
              size="xs"
              onClick={() => markInvoicePaid(invoice.id)}
              disabled={invoiceUpdateLoading}
            />
          ) : null
        }
      />
    </div>
  );

  const planContent = (
    <div className="space-y-3">
      <Card
        header={<CardHeader title="Plan & add-ons" />}
        content={
          <div className="text-sm text-gray-600 space-y-2">
            <div>
              Product access is still driven by plan tier and tenant feature flags. Payment
              collection is separate so this tenant can stay manual or move to Stripe self-serve.
            </div>
            <div>
              Use checkout for first-time activation or the Stripe portal for payment method and
              hosted subscription management.
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {selfServeOffers.map((catalogOffer) => {
                const isCurrentPlan = currentPlanKey === catalogOffer.key;

                return (
                  <Card
                    key={catalogOffer.key}
                    header={<CardHeader title={catalogOffer.name} />}
                    content={
                      <div className="space-y-3">
                        <div>{catalogOffer.description}</div>
                        <div className="text-xs text-gray-500">
                          Product tier: {formatLabel(catalogOffer.planTier)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Collection method: {formatCollectionMethod(catalogOffer.collectionMethod)}
                        </div>
                        {catalogOffer.isLegacy ? (
                          <div className="text-xs text-amber-600">
                            Legacy founding offer kept for backward compatibility.
                          </div>
                        ) : null}
                        <div className="flex gap-2">
                          {canStartCheckout ? (
                            <Button
                              size="sm"
                              onClick={() => startCheckout(catalogOffer.key)}
                              disabled={checkoutLoading}
                            >
                              {checkoutLoading && isCurrentPlan
                                ? "Opening..."
                                : isCurrentPlan
                                ? "Choose current plan"
                                : `Choose ${catalogOffer.name}`}
                            </Button>
                          ) : null}
                          {canManagePortal && isCurrentPlan ? (
                            <Button
                              type="alternative"
                              size="sm"
                              onClick={openBillingPortal}
                              disabled={portalLoading}
                            >
                              {portalLoading ? "Opening..." : "Manage subscription"}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    }
                  />
                );
              })}
            </div>
            {selfServeOffers.length === 0 ? (
              <div className="border rounded-lg p-3 bg-gray-50 text-sm text-gray-600">
                No self-serve Stripe plans are configured yet for this environment.
              </div>
            ) : null}
            <div className="flex gap-2">
              {canManagePortal ? (
                <Button
                  type="alternative"
                  size="sm"
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                >
                  {portalLoading ? "Opening..." : "Manage subscription"}
                </Button>
              ) : null}
            </div>
          </div>
        }
      />
    </div>
  );
  const billingTabs = [
    { id: "overview", title: "Overview", content: overviewContent },
    { id: "invoices", title: "Invoices", content: invoicesContent },
    ...(isManualBillingCollection(collectionMethod)
      ? []
      : [{ id: "plan", title: "Plan & add-ons", content: planContent }]),
  ];
  const requestedBillingTabId = new URLSearchParams(location.search).get("customerBillingTab");
  const selectedBillingTabId = billingTabs.some((tab) => tab.id === requestedBillingTabId)
    ? requestedBillingTabId
    : billingTabs[0]?.id;

  function handleBillingTabChange(item) {
    const tabId = item?.id;
    if (!tabId) return;

    const nextParams = new URLSearchParams(location.search);
    nextParams.set("customerBillingTab", tabId);
    const nextSearch = nextParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash || "",
      },
      { replace: true }
    );
  }

  if (!customerId) {
    return (
      <Card>
        <div className="text-sm text-gray-600">
          Select a tenant to review billing details.
        </div>
      </Card>
    );
  }

  return (
    <Tabs
      id="customerBillingTab"
      tabListClasses="border-b flex gap-2"
      onChangeTab={handleBillingTabChange}
      selectedId={selectedBillingTabId}
      data={billingTabs}
    />
  );
};

export default CustomerBilling;
